import type { SupabaseClient } from '@supabase/supabase-js';
import { GAMBIO_SHIPPING_STATUS } from '../../lib/gambio-product-field-catalogs.js';
import type { ConflictRow } from '../../lib/variant-collision-text.js';

export const DEFAULT_SHOP = 'default';

export interface SettingsCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

export interface ShopRow {
  shop_key: string; label: string | null; status: string;
  base_url: string | null; vario_channel: { id?: string; label?: string } | null;
}
export interface VarioOption { id: string; label: string }
export interface VarioWarehouse { id: string; number: string | null; displayName: string | null }
export interface PaymentOptions { methods: VarioOption[]; terms: VarioOption[]; deliveryMethods: VarioOption[]; deliveryTerms: VarioOption[] }
export interface CountryRow { iso2: string; gambio_country_id: number; name: string | null; updated_at: string }
export interface ChannelOption { id: string; label: string; assignedToShopKey: string | null; assignedToLabel: string | null }

export function fnUrl(ctx: SettingsCtx, slug: string): string {
  return `${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/${slug}`;
}
async function post<T>(ctx: SettingsCtx, slug: string, body: unknown): Promise<T> {
  const res = await fetch(fnUrl(ctx, slug), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    // Die Meldung aus dem Fehler-Body durchreichen, statt sie wegzuwerfen: unsere Functions
    // antworten `{ error: { code, message } }`, und dort steht der einzige Text, der dem Anwender
    // weiterhilft (z. B. „Der Optionswert „Schwarz" wird von 4 Varianten verwendet: …"). Vorher
    // stand im Toast nur „HTTP 502" — Symptom ohne Ursache (Vorfall 2026-08-17, Optionswert löschen).
    const text = await res.text().catch(() => '');
    let message = '';
    try {
      const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
      message = parsed.error?.message ?? '';
    } catch { /* kein JSON → Rohtext unten als Fallback */ }
    if (!message) message = text.slice(0, 300);
    throw new Error(message || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function loadShops(ctx: SettingsCtx): Promise<ShopRow[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_list_shops');
  if (error) throw new Error(error.message);
  return (data ?? []) as ShopRow[];
}
export async function loadShopSettings(ctx: SettingsCtx, shopKey: string): Promise<Record<string, unknown> | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_shop_settings', { _shop_key: shopKey });
  if (error) return null;
  return (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null;
}
export async function loadWarehouses(ctx: SettingsCtx): Promise<VarioWarehouse[]> {
  try {
    const p = await post<{ ok?: boolean; warehouses?: VarioWarehouse[] }>(ctx, 'vario-warehouses-list', { marketplace: 'gambio' });
    return p.warehouses ?? [];
  } catch { return []; }
}
export async function loadPaymentOptions(ctx: SettingsCtx): Promise<PaymentOptions> {
  try {
    const p = await post<Partial<PaymentOptions>>(ctx, 'vario-payment-options-list', { marketplace: 'gambio' });
    return { methods: p.methods ?? [], terms: p.terms ?? [], deliveryMethods: p.deliveryMethods ?? [], deliveryTerms: p.deliveryTerms ?? [] };
  } catch { return { methods: [], terms: [], deliveryMethods: [], deliveryTerms: [] }; }
}
export async function loadChannels(ctx: SettingsCtx): Promise<ChannelOption[]> {
  const p = await post<{ ok?: boolean; channels?: ChannelOption[] }>(ctx, 'gambio-channels-list', {});
  return p.channels ?? [];
}
export async function loadCountryMap(ctx: SettingsCtx, shopKey: string): Promise<CountryRow[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_country_map', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  return (data ?? []) as CountryRow[];
}
export async function assignChannel(ctx: SettingsCtx, shopKey: string, channelId: string): Promise<{ ok: boolean; label?: string; error?: string }> {
  try {
    const p = await post<{ ok?: boolean; label?: string; error?: { message: string } }>(ctx, 'gambio-channel-add', { shopKey, channelId });
    return {
      ok: !!p.ok,
      ...(p.label !== undefined ? { label: p.label } : {}),
      ...(p.error?.message !== undefined ? { error: p.error.message } : {}),
    };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}
export async function defaultShop(ctx: SettingsCtx): Promise<ShopRow | null> {
  const shops = await loadShops(ctx);
  return shops.find((s) => s.shop_key === DEFAULT_SHOP) ?? shops[0] ?? null;
}

export interface CgPayload {
  varioPriceGroups?: Array<{ id: string; label: string }>;
  varioCustomerGroups?: Array<{ id: string; label: string }>;
  gambioCustomerGroups?: Array<{ statusId: number; label: string }>;
  /** Shop-Schalter „Kundengruppencheck" (GROUP_CHECK). null = nicht ermittelbar → UI zeigt nichts. */
  groupCheckActive?: boolean | null;
  /** Shop-Basis-URL aus der Connection, fuer den Deeplink in die Gambio-Konfiguration. */
  shopBaseUrl?: string | null;
  mappings?: Array<{ varioId: string; varioLabel: string | null; varioCustomerGroupId: string | null; varioCustomerGroupLabel: string | null; gambioId: number | null; gambioLabel: string | null }>;
}
/** Zustand des Bestellimports: welche Bestellnummer als naechste geholt wird. */
export interface OrderBackfillCursor {
  /** Naechste zu importierende Bestellnummer. null = es wurde noch nichts importiert. */
  nextOrderId: number | null;
  /** Hoechste bereits importierte Bestellnummer. */
  highestImported: number | null;
  /** Anzahl importierter Bestellungen. */
  ordersImported: number;
  lastDeltaRunAt: string | null;
  lastFullRunAt: string | null;
}

/** Liest den Bestellimport-Zeiger (RPC aus Migration 0335). */
export async function loadOrderBackfillCursor(ctx: SettingsCtx, shopKey: string): Promise<OrderBackfillCursor> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_order_backfill_cursor', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  const num = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    const n = Number(v);                 // bigint kommt als String aus PostgREST
    return Number.isFinite(n) ? n : null;
  };
  return {
    nextOrderId: num(row?.['next_order_id']),
    highestImported: num(row?.['highest_imported']),
    ordersImported: num(row?.['orders_imported']) ?? 0,
    lastDeltaRunAt: (row?.['last_delta_run_at'] as string | null) ?? null,
    lastFullRunAt: (row?.['last_full_run_at'] as string | null) ?? null,
  };
}

/** Setzt den Bestellimport-Zeiger. Zuruecksetzen holt die betroffenen Bestellungen erneut voll. */
export async function saveOrderBackfillCursor(ctx: SettingsCtx, shopKey: string, nextOrderId: number): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_order_backfill_cursor', {
    _shop_key: shopKey, _next_order_id: nextOrderId,
  });
  if (error) throw new Error(error.message);
}

export async function loadCustomerGroups(ctx: SettingsCtx, shopKey: string): Promise<CgPayload> {
  return post<CgPayload>(ctx, 'gambio-customer-groups', { action: 'list', shopKey });
}
export async function createPriceGroup(ctx: SettingsCtx, shopKey: string, label: string): Promise<{ id: string | null; label: string }> {
  return post<{ id: string | null; label: string }>(ctx, 'gambio-customer-groups', { action: 'create-price-group', shopKey, label });
}
export async function createCustomerGroup(ctx: SettingsCtx, shopKey: string, label: string): Promise<{ id: string | null; label: string }> {
  return post<{ id: string | null; label: string }>(ctx, 'gambio-customer-groups', { action: 'create-customer-group', shopKey, label });
}
export async function setCustomerGroup(ctx: SettingsCtx, shopKey: string, args: {
  varioPriceGroupId: string | null; varioPriceGroupLabel: string | null;
  gambioStatusId: number | null; gambioStatusLabel: string | null;
  varioCustomerGroupId: string | null; varioCustomerGroupLabel: string | null;
}): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_customer_group_mapping', {
    _shop_key: shopKey,
    _vario_price_group_id: args.varioPriceGroupId, _vario_price_group_label: args.varioPriceGroupLabel,
    _gambio_status_id: args.gambioStatusId, _gambio_status_label: args.gambioStatusLabel,
    _vario_customer_group_id: args.varioCustomerGroupId, _vario_customer_group_label: args.varioCustomerGroupLabel,
  });
  if (error) throw new Error(error.message);
}
/**
 * Stösst den Kategorie-Abgleich an und liefert die Job-ID zurück, damit der Aufrufer den Lauf
 * abwarten und die Ansicht danach nachziehen kann (`awaitJob`). Wer das Ergebnis nicht braucht,
 * ignoriert den Rückgabewert einfach — die Anlage-/Speichern-Pfade laden ihren Baum ohnehin
 * direkt aus Gambio neu, dort ist der Vario-Lauf nicht die Quelle der Anzeige.
 */
export async function enqueueCategorySync(ctx: SettingsCtx, shopKey: string): Promise<string | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_sync_categories', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const id = (Array.isArray(data) ? data[0] : data) as string | null;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** Zieht die Gambio-Bilder aller Variantengruppen des Shops nach Vario nach (Nachtrags-Import).
 *  `null` als Produktliste heisst „alle Gruppen"; ein leeres Array wuerde die RPC ebenso
 *  behandeln, wird hier aber gar nicht erst gesendet. */
/**
 * Legt in VARIO eine Inventur mit den Gambio-Bestaenden als Zaehlliste an (Anfangsbestand).
 * Liefert die Job-Id, damit der Aufrufer auf das Ergebnis warten kann.
 */
export async function enqueueStockInventory(
  ctx: SettingsCtx, shopKey: string, art: 'INITIAL' | 'CLOSING_DATE',
): Promise<string> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_stock_inventory', {
    _shop_key: shopKey,
    _art: art,
  });
  if (error) throw new Error(error.message);
  const id = typeof data === 'string' ? data : (data as { job_id?: string } | null)?.job_id;
  if (!id) throw new Error('Es wurde kein Job angelegt.');
  return id;
}

export async function enqueueVariantImageImport(ctx: SettingsCtx, shopKey: string): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_enqueue_import_variant_images', {
    _shop_key: shopKey,
    _gambio_product_ids: null,
  });
  if (error) throw new Error(error.message);
}

/** Ruft die Edge-Function `gambio-category-admin` (Kategorien-Editor) auf.
 *  Happy-Path-Aufrufe (get-tree/get-detail/create/update/move/upload-image/delete-image).
 *  `shopKey` wird auf DEFAULT_SHOP vorbelegt. Fehler (inkl. HTTP-Status) wirft `post`. */
export async function callCategoryAdmin<T = unknown>(ctx: SettingsCtx, body: Record<string, unknown>): Promise<T> {
  return post<T>(ctx, 'gambio-category-admin', { shopKey: DEFAULT_SHOP, ...body });
}

/** Legt einen Vario-Kontakt-Feldtyp an (POST /erp/contact-types, via Worker-Job). Gibt die Job-ID zurueck (fuer awaitJob). */
export async function enqueueCreateContactType(ctx: SettingsCtx, shopKey: string, label: string, type = 'CUSTOM'): Promise<string | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_create_contact_type', { _shop_key: shopKey, _label: label, _type: type });
  if (error) throw new Error(error.message);
  return typeof data === 'string' ? data : (Array.isArray(data) ? ((data[0] as string) ?? null) : null);
}

export interface DiscoveredModule { gambioModule: string; sampleLabel: string | null; seenCount: number }
export interface OrderModalityRow {
  gambioModule: string; gambioLabel: string | null;
  varioMethodId: string | null; varioMethodLabel: string | null;
  varioTermId: string | null; varioTermLabel: string | null;
  gambioParcelServiceId?: string | null; gambioParcelServiceLabel?: string | null;
}
export type ChannelMappingKind = 'payment'|'shipping'|'tax_class'|'order_status'|'carrier';

export async function discoverModalities(ctx: SettingsCtx, shopKey: string, kind: 'payment'|'shipping'): Promise<DiscoveredModule[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_discover_order_modalities', { _shop_key: shopKey, _kind: kind });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r: Record<string, unknown>) => ({ gambioModule: String(r['gambio_module']), sampleLabel: r['sample_label'] != null ? String(r['sample_label']) : null, seenCount: Number(r['seen_count'] ?? 0) }));
}
export async function loadChannelMappings(ctx: SettingsCtx, shopKey: string, kind: ChannelMappingKind): Promise<OrderModalityRow[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_channel_mappings', { _shop_key: shopKey, _kind: kind });
  if (error) throw new Error(error.message);
  return (data ?? []).map((m: Record<string, unknown>) => ({
    gambioModule: String(m['gambio_module']), gambioLabel: (m['gambio_label'] as string) ?? null,
    varioMethodId: (m['vario_method_id'] as string) ?? null, varioMethodLabel: (m['vario_method_label'] as string) ?? null,
    varioTermId: (m['vario_term_id'] as string) ?? null, varioTermLabel: (m['vario_term_label'] as string) ?? null,
    gambioParcelServiceId: (m['gambio_parcel_service_id'] as string) ?? null, gambioParcelServiceLabel: (m['gambio_parcel_service_label'] as string) ?? null,
  }));
}
export async function setChannelMapping(ctx: SettingsCtx, shopKey: string, kind: ChannelMappingKind, gambioModule: string, gambioLabel: string | null, args: { methodId: string|null; methodLabel: string|null; termId: string|null; termLabel: string|null; parcelServiceId?: string|null; parcelServiceLabel?: string|null }): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_channel_mapping', {
    _shop_key: shopKey, _kind: kind, _gambio_module: gambioModule, _gambio_label: gambioLabel,
    _vario_method_id: args.methodId, _vario_method_label: args.methodLabel,
    _vario_term_id: args.termId, _vario_term_label: args.termLabel,
    _gambio_parcel_service_id: args.parcelServiceId ?? null, _gambio_parcel_service_label: args.parcelServiceLabel ?? null,
  });
  if (error) throw new Error(error.message);
}

export interface TaxClassOptions {
  gambioTaxClasses: Array<{ id: string; title: string }>;
  varioTaxSchemas: Array<{ id: string; label: string }>;
}
export async function loadTaxClassOptions(ctx: SettingsCtx, shopKey: string): Promise<TaxClassOptions> {
  try {
    const p = await post<TaxClassOptions>(ctx, 'gambio-tax-classes', { shopKey });
    return { gambioTaxClasses: p.gambioTaxClasses ?? [], varioTaxSchemas: p.varioTaxSchemas ?? [] };
  } catch { return { gambioTaxClasses: [], varioTaxSchemas: [] }; }
}

export interface OrderStatusOptions { orderStatuses: Array<{ id: string; label: string }> }
export async function loadOrderStatusOptions(ctx: SettingsCtx, shopKey: string): Promise<OrderStatusOptions> {
  try {
    const p = await post<OrderStatusOptions>(ctx, 'gambio-order-statuses', { shopKey });
    return { orderStatuses: p.orderStatuses ?? [] };
  } catch { return { orderStatuses: [] }; }
}

export interface ParcelServiceOptions { parcelServices: Array<{ id: string; name: string }> }
export async function loadParcelServiceOptions(ctx: SettingsCtx, shopKey: string): Promise<ParcelServiceOptions> {
  try {
    const p = await post<ParcelServiceOptions>(ctx, 'gambio-parcel-services', { shopKey });
    return { parcelServices: p.parcelServices ?? [] };
  } catch { return { parcelServices: [] }; }
}

/** Lädt die Vario-Kontakt-Feldtypen (GPSR-Ansprechpartner-Rollen) für die Dropdown-Zuordnung.
 *  Liefert zusätzlich die tenant-spezifische Vario-Basis-URL für den Deep-Link zur Kontakttyp-Verwaltung. */
export async function loadContactTypeOptions(ctx: SettingsCtx, shopKey: string): Promise<{ items: VarioOption[] }> {
  try {
    const p = await post<{ items?: VarioOption[] }>(ctx, 'gambio-contact-types-list', { shopKey });
    return { items: p.items ?? [] };
  } catch { return { items: [] }; }
}

export interface BasePriceUnitData {
  gambioUnits: Array<{ id: number; name: string }>;
  varioUnits: Array<{ id: string; abbr: string; label: string }>;
  mappings: Array<{ gambio_vpe_id: number; gambio_vpe_name: string | null; vario_unit_abbr: string | null }>;
  suggestions: Array<{ vpeId: number; varioAbbr: string | null }>;
  // Mengeneinheit (QuantityUnit, separater Gambio-Katalog /quantity_units).
  gambioQuantityUnits: Array<{ id: number; name: string }>;
  quantityMappings: Array<{ gambio_quantity_unit_id: number; gambio_quantity_unit_name: string | null; vario_unit_abbr: string | null; vario_unit_id?: string | null }>;
  quantitySuggestions: Array<{ quantityUnitId: number; varioAbbr: string | null }>;
}
export async function loadBasePriceUnits(ctx: SettingsCtx, shopKey: string): Promise<BasePriceUnitData> {
  try {
    const p = await post<Partial<BasePriceUnitData>>(ctx, 'gambio-base-price-units', { shopKey });
    return {
      gambioUnits: p.gambioUnits ?? [], varioUnits: p.varioUnits ?? [], mappings: p.mappings ?? [], suggestions: p.suggestions ?? [],
      gambioQuantityUnits: p.gambioQuantityUnits ?? [], quantityMappings: p.quantityMappings ?? [], quantitySuggestions: p.quantitySuggestions ?? [],
    };
  } catch { return { gambioUnits: [], varioUnits: [], mappings: [], suggestions: [], gambioQuantityUnits: [], quantityMappings: [], quantitySuggestions: [] }; }
}
export async function setBasePriceUnitMapping(ctx: SettingsCtx, shopKey: string, vpeId: number, vpeName: string | null, varioAbbr: string | null): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_base_price_unit_mapping', { _shop_key: shopKey, _vpe_id: vpeId, _vpe_name: vpeName, _vario_abbr: varioAbbr });
  if (error) throw new Error(error.message);
}
/**
 * Speichert eine Mengeneinheiten-Zuordnung. `varioUnitId` seit Migration 0356: mit Id erspart sich
 * der Worker beim Import je Artikel den Lookup des kompletten VARIO-Einheitenkatalogs. Ohne Id
 * (Bestandszeile) faellt er auf die Abkuerzung zurueck.
 */
export async function setQuantityUnitMapping(ctx: SettingsCtx, shopKey: string, quId: number, quName: string | null, varioAbbr: string | null, varioUnitId: string | null = null): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_quantity_unit_mapping', {
    _shop_key: shopKey, _qu_id: quId, _qu_name: quName, _vario_abbr: varioAbbr,
    _vario_unit_id: varioUnitId,
  });
  if (error) throw new Error(error.message);
}

/** CRUD auf den Gambio-Einheiten-Katalogen (VPE + Mengeneinheit) via Edge-Fn gambio-unit-catalog-admin. */
export async function unitCatalogAdmin(
  ctx: SettingsCtx,
  shopKey: string,
  args: { action: 'create' | 'rename' | 'delete'; catalog: 'vpe' | 'quantity'; id?: number; nameDe?: string; nameEn?: string },
): Promise<{ ok: boolean; id?: number }> {
  const res = await fetch(fnUrl(ctx, 'gambio-unit-catalog-admin'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: JSON.stringify({ shopKey, ...args }),
  });
  const data = (await res.json().catch(() => null)) as { ok?: boolean; id?: number; error?: { message?: string } } | null;
  if (!res.ok) throw new Error(data?.error?.message || `HTTP ${res.status}`);
  return { ok: !!data?.ok, ...(data?.id != null ? { id: data.id } : {}) };
}

/** Stoesst den Laender-Abgleich an und liefert die Job-ID zurueck (wie `enqueueCategorySync`),
 *  damit der Aufrufer den Lauf abwarten und die Ansicht danach nachziehen kann. */
/** Offene Zuordnungen je Abschnitt in EINEM Aufruf (Migration 0338). Abschnitte, deren
 *  Gegenseite nur live existiert (Einheiten, Hersteller, Varianten), liefert die RPC bewusst
 *  nicht — die trägt das Frontend nach, sobald der Abschnitt einmal offen war. */
export async function loadMappingOpenCounts(ctx: SettingsCtx, shopKey: string): Promise<Record<string, number>> {
  const { data, error } = await ctx.supabase.rpc('gambio_mapping_open_counts', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const rows = (Array.isArray(data) ? data : []) as Array<{ section?: string; open_count?: number }>;
  const out: Record<string, number> = {};
  for (const r of rows) {
    if (typeof r.section === 'string' && typeof r.open_count === 'number') out[r.section] = r.open_count;
  }
  return out;
}

export async function enqueueCountrySync(ctx: SettingsCtx, shopKey: string): Promise<string | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_sync_countries', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const id = (Array.isArray(data) ? data[0] : data) as string | null;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

// Stößt den proaktiven Vario-Carrier-Sync an (Worker-Job gambio.sync_carriers) - holt ALLE in
// VARIO angelegten Versanddienstleister in die „Paketdienste"-Zuordnung, nicht erst nach Versand.
export async function enqueueCarrierSync(ctx: SettingsCtx, shopKey: string): Promise<string | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_sync_carriers', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const id = (Array.isArray(data) ? data[0] : data) as string | null;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

export interface CurrencyDiffRow {
  iso: string; name: string; gambioActive: boolean; varioActive: boolean;
  varioId: string | null; status: 'both' | 'onlyGambio' | 'onlyVario';
}
export async function loadCurrencyDiff(ctx: SettingsCtx, shopKey: string): Promise<{ rows: CurrencyDiffRow[]; gambioAdminBaseUrl: string }> {
  const p = await post<Partial<{ rows: CurrencyDiffRow[]; gambioAdminBaseUrl: string }>>(ctx, 'gambio-channel-currencies', { action: 'list', shopKey });
  return { rows: p.rows ?? [], gambioAdminBaseUrl: p.gambioAdminBaseUrl ?? '' };
}
export async function activateVarioCurrency(ctx: SettingsCtx, shopKey: string, iso: string): Promise<void> {
  await post<{ ok?: boolean; iso?: string; varioId?: string }>(ctx, 'gambio-channel-currencies', { action: 'activate', shopKey, iso });
}

export interface ManufacturerOptions {
  gambioManufacturers: Array<{ id: number; name: string }>;
  varioManufacturers: Array<{ accountId: string; label: string }>;
  suggestions: Record<string, string>;
}
export async function loadManufacturerOptions(ctx: SettingsCtx, shopKey: string): Promise<ManufacturerOptions> {
  try {
    const p = await post<Partial<ManufacturerOptions>>(ctx, 'gambio-manufacturers', { shopKey });
    return { gambioManufacturers: p.gambioManufacturers ?? [], varioManufacturers: p.varioManufacturers ?? [], suggestions: p.suggestions ?? {} };
  } catch { return { gambioManufacturers: [], varioManufacturers: [], suggestions: {} }; }
}

export interface ManufacturerMappingRow {
  gambio_manufacturer_id: number;
  gambio_manufacturer_name: string | null;
  vario_account_id: string | null;
  vario_account_label: string | null;
}
export async function getManufacturerMappings(ctx: SettingsCtx, shopKey: string): Promise<ManufacturerMappingRow[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_manufacturer_mappings', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  return (data ?? []) as ManufacturerMappingRow[];
}
export async function setManufacturerMapping(ctx: SettingsCtx, shopKey: string, gambioId: number, gambioName: string | null, varioAccountId: string, varioAccountLabel: string | null): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_manufacturer_mapping', {
    _shop_key: shopKey,
    _gambio_manufacturer_id: gambioId,
    _gambio_manufacturer_name: gambioName,
    _vario_account_id: varioAccountId,
    _vario_account_label: varioAccountLabel,
  });
  if (error) throw new Error(error.message);
}
export async function deleteManufacturerMapping(ctx: SettingsCtx, shopKey: string, gambioId: number): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_delete_manufacturer_mapping', { _shop_key: shopKey, _gambio_manufacturer_id: gambioId });
  if (error) throw new Error(error.message);
}

// ── Produktfeld-Mappings (Produkttyp + Lieferstatus, Vario→Gambio) ────────────
export interface ProductFieldMappingRow {
  vario_value: string;
  vario_label: string | null;
  gambio_value: string | null;
  gambio_label: string | null;
}
export type ProductFieldMappingKind = 'product_type' | 'shipping_status';
export async function getProductFieldMappings(ctx: SettingsCtx, shopKey: string, kind: ProductFieldMappingKind): Promise<ProductFieldMappingRow[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_product_field_mappings', { _shop_key: shopKey, _kind: kind });
  if (error) throw new Error(error.message);
  return (data ?? []) as ProductFieldMappingRow[];
}
export async function setProductFieldMapping(ctx: SettingsCtx, shopKey: string, kind: ProductFieldMappingKind, varioValue: string, varioLabel: string | null, gambioValue: string, gambioLabel: string | null): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_product_field_mapping', {
    _shop_key: shopKey,
    _kind: kind,
    _vario_value: varioValue,
    _vario_label: varioLabel,
    _gambio_value: gambioValue,
    _gambio_label: gambioLabel,
  });
  if (error) throw new Error(error.message);
}
export async function deleteProductFieldMapping(ctx: SettingsCtx, shopKey: string, kind: ProductFieldMappingKind, varioValue: string): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_delete_product_field_mapping', { _shop_key: shopKey, _kind: kind, _vario_value: varioValue });
  if (error) throw new Error(error.message);
}

// ── Lokaler Gambio-Lieferstatus-Katalog (shippingTimeId → Bezeichnung) ─────────
// Gambio bietet keinen API-Katalog dafür (Migr. 0312). Bei leerem Katalog liefern wir die
// bisherigen Standardwerte (GAMBIO_SHIPPING_STATUS) als Fallback zurück (persisted=false),
// damit die Dropdowns nie leer sind, ohne stillen DB-Write.
export interface ShippingStatusCatalogEntry {
  gambio_value: string;
  label: string;
  sort_order: number;
  persisted: boolean;
}
export async function loadShippingStatusCatalog(ctx: SettingsCtx, shopKey: string): Promise<ShippingStatusCatalogEntry[]> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_shipping_status_catalog', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ gambio_value: string; label: string; sort_order: number }>;
  if (rows.length > 0) return rows.map((r) => ({ ...r, persisted: true }));
  return GAMBIO_SHIPPING_STATUS.map((o, i) => ({ gambio_value: o.value, label: o.label, sort_order: i, persisted: false }));
}
export async function saveShippingStatusCatalogEntry(ctx: SettingsCtx, shopKey: string, gambioValue: string, label: string, sortOrder: number): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_shipping_status_catalog', {
    _shop_key: shopKey, _gambio_value: gambioValue, _label: label, _sort_order: sortOrder,
  });
  if (error) throw new Error(error.message);
}
export async function deleteShippingStatusCatalogEntry(ctx: SettingsCtx, shopKey: string, gambioValue: string): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_delete_shipping_status_catalog', { _shop_key: shopKey, _gambio_value: gambioValue });
  if (error) throw new Error(error.message);
}

export async function loadShopPublicCreds(ctx: SettingsCtx, shopKey: string): Promise<{ base_url: string | null; username: string | null } | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_get_shop_public_credentials', { _shop_key: shopKey });
  if (error) return null;
  const row = Array.isArray(data) ? data[0] : data;
  return row ? { base_url: row.base_url ?? null, username: row.username ?? null } : null;
}
export async function saveCredentials(ctx: SettingsCtx, baseUrl: string, username: string, password: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const p = await post<{ ok?: boolean; error?: { message: string } }>(ctx, 'gambio-install', { baseUrl, username, password });
    return { ok: !!p.ok, ...(p.error?.message !== undefined ? { error: p.error.message } : {}) };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}
export async function createChannel(ctx: SettingsCtx, shopKey: string): Promise<{ ok: boolean; label?: string; alreadyPresent?: boolean; error?: string }> {
  try {
    const p = await post<{ ok?: boolean; label?: string; alreadyPresent?: boolean; error?: { message: string } }>(ctx, 'gambio-channel-add', { shopKey });
    return {
      ok: !!p.ok,
      ...(p.label !== undefined ? { label: p.label } : {}),
      ...(p.alreadyPresent !== undefined ? { alreadyPresent: p.alreadyPresent } : {}),
      ...(p.error?.message !== undefined ? { error: p.error.message } : {}),
    };
  } catch (e) { return { ok: false, error: (e as Error).message }; }
}

/** Voll-Upsert der Shop-Settings: cur als Basis, patch (Spaltennamen) ueberschreibt.
 *  Buendelt die monolithische RPC gambio_set_shop_settings an EINER Stelle, damit keine
 *  Sektion versehentlich Fremdfelder auf Default zuruecksetzt. */
export async function saveShopSettings(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  patch: Record<string, unknown>,
): Promise<void> {
  const m = { ...cur, ...patch };
  const { error } = await ctx.supabase.rpc('gambio_set_shop_settings', {
    _shop_key: shopKey,
    _vario_storage_id: m['vario_storage_id'] ?? null,
    _vario_storage_label: m['vario_storage_label'] ?? null,
    _vario_payment_method_id: m['vario_payment_method_id'] ?? null,
    _vario_payment_method_label: m['vario_payment_method_label'] ?? null,
    _vario_payment_term_id: m['vario_payment_term_id'] ?? null,
    _vario_payment_term_label: m['vario_payment_term_label'] ?? null,
    _vario_delivery_method_id: m['vario_delivery_method_id'] ?? null,
    _vario_delivery_method_label: m['vario_delivery_method_label'] ?? null,
    _vario_delivery_term_id: m['vario_delivery_term_id'] ?? null,
    _vario_delivery_term_label: m['vario_delivery_term_label'] ?? null,
    _suppress_stock_push: m['suppress_stock_push'] ?? false,
    _auto_takeover_orders: m['auto_takeover_orders'] ?? false,
    _customer_number_from_gambio: m['customer_number_from_gambio'] ?? false,
    _sync_direction: m['sync_direction'] ?? 'vario_to_gambio',
    _import_write_master: m['import_write_master'] ?? false,
    _import_overwrite_master: m['import_overwrite_master'] ?? false,
    _gpsr_description_mode: m['gpsr_description_mode'] ?? 'off',
    _default_shipping_time_id: m['default_shipping_time_id'] ?? 1,
    _virtual_stock: m['virtual_stock'] ?? 9999,
    _export_customers_enabled: m['export_customers_enabled'] ?? false,
    _export_customers_create_new: m['export_customers_create_new'] ?? false,
    _import_additional_to_variants: m['import_additional_to_variants'] ?? true,
    _display_defaults: m['display_defaults'] ?? {},
    _variant_price_type_mode: m['variant_price_type_mode'] ?? 'fixed',
    _variant_weight_mode: m['variant_weight_mode'] ?? 'add',
    _gpsr_contact_map: m['gpsr_contact_map'] ?? {},
    _default_active: m['default_active'] ?? true,
    _deactivate_on_unassign: m['deactivate_on_unassign'] ?? true,
    _import_write_default_channel_price: m['import_write_default_channel_price'] ?? false,
    // Fehlt der Wert (alter Settings-Datensatz), gilt der Auslieferungszustand: alle weichen
    // Regeln AUSSER VPE. `null` zu schicken wuerde sie auf den RPC-Default zurueckdrehen, was hier
    // dasselbe ist - aber eine LEERE Auswahl muss erhalten bleiben, deshalb nur bei fehlendem Wert
    // ersetzen.
    _readiness_optional_checks: m['readiness_optional_checks'] ?? ['description', 'manufacturer', 'weight'],
  });
  if (error) throw new Error(error.message);
}

export async function saveLager(ctx: SettingsCtx, shopKey: string, cur: Record<string, unknown>, patch: { storageId: string | null; storageLabel: string | null; suppressStock: boolean; virtualStock: number }): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, {
    vario_storage_id: patch.storageId,
    vario_storage_label: patch.storageLabel,
    suppress_stock_push: patch.suppressStock,
    virtual_stock: patch.virtualStock,
  });
}

export async function saveImport(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  patch: {
    importWriteMaster: boolean;
    importOverwriteMaster: boolean;
    importAdditionalToVariants: boolean;
    importWriteDefaultChannelPrice: boolean;
    gpsrDescriptionMode: string;
    gpsrContactMap?: Record<string, Record<string, string>>;
  },
): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, {
    import_write_master: patch.importWriteMaster,
    import_overwrite_master: patch.importOverwriteMaster,
    import_additional_to_variants: patch.importAdditionalToVariants,
    import_write_default_channel_price: patch.importWriteDefaultChannelPrice,
    gpsr_description_mode: patch.gpsrDescriptionMode,
    gpsr_contact_map: patch.gpsrContactMap ?? {},
  });
}

/**
 * Speichert, welche WEICHEN Regeln die Anlage-Pruefung nutzt. Die harten (Kategorie, Bezeichnung,
 * Preis, Steuerschema) stehen nicht in der Liste - sie gelten immer und sind nicht abwaehlbar.
 */
export async function saveAnlagepruefung(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  patch: { optionaleChecks: string[] },
): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, { readiness_optional_checks: patch.optionaleChecks });
}

/** Speichert NUR die GPSR-Felder (Beschreibungsmodus + Kontakt-Zuordnung); cur haelt alle uebrigen Settings unveraendert. */
export async function saveGpsr(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  patch: {
    gpsrDescriptionMode: string;
    gpsrContactMap?: Record<string, Record<string, string>>;
  },
): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, {
    gpsr_description_mode: patch.gpsrDescriptionMode,
    gpsr_contact_map: patch.gpsrContactMap ?? {},
  });
}

/**
 * Speichert die Varianten-Berechnung. Nur noch der Preistyp: den Gewichtsmodus gibt es seit
 * 2026-08-17 nicht mehr, weil Gambio Gewichte zur Laufzeit IMMER addiert (siehe
 * `resolveVariantWeight`). Die Spalte `variant_weight_mode` bleibt als Altlast unangetastet stehen.
 */
export async function saveVariantModes(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  patch: { variantPriceTypeMode: string },
): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, {
    variant_price_type_mode: patch.variantPriceTypeMode,
  });
}

export async function saveDisplayDefaults(
  ctx: SettingsCtx,
  shopKey: string,
  cur: Record<string, unknown>,
  defaults: Record<string, unknown>,
): Promise<void> {
  await saveShopSettings(ctx, shopKey, cur, { display_defaults: defaults });
}

/** Stösst den Gambio→Vario-Kundenimport an (Fanout-Job). Gibt die Job-ID zurueck (fuer Polling). */
export async function enqueueCustomerImport(ctx: SettingsCtx, shopKey: string): Promise<string | null> {
  const { data, error } = await ctx.supabase.rpc('gambio_enqueue_customer_import', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
  return Array.isArray(data) ? (data[0]?.job_id ?? null) : ((data as { job_id?: string } | null)?.job_id ?? null);
}

/** Stösst den Vario→Gambio-Kundenexport an (vorgemerkte Kunden). */
export async function enqueueCustomerExport(ctx: SettingsCtx, shopKey: string): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_enqueue_customer_export', { _shop_key: shopKey });
  if (error) throw new Error(error.message);
}

// ── Varianten-Zuordnung (Gambio-Optionen ↔ Vario-Attribute/-Werte) ────────────
// Matrix aus der Edge-Function gambio-variant-mapping-data; Overrides via RPC.
export interface VarioVariantValue { id: string; label: string; abbreviation: string }
export interface VarioVariantAttribute { id: string; label: string; values: VarioVariantValue[] }
export interface GambioVariantValue { ovid: string; label: string; modelNumber: string }
export interface GambioVariantOption { optionId: string; name: string; values: GambioVariantValue[] }
export interface VariantAttrMap { gambioOptionId: string; varioAttributeId: string; source: string }
export interface VariantValMap { gambioOptionValueId: string; gambioOptionId: string; varioAttributeId: string; varioValueId: string; source: string }
export interface VariantMatrix {
  ok: boolean;
  direction: string;
  varioAttributes: VarioVariantAttribute[];
  gambioOptions: GambioVariantOption[];
  attributeMap: VariantAttrMap[];
  valueMap: VariantValMap[];
  error?: { message: string };
}

/** Lädt die Varianten-Mapping-Matrix (Vario-Attribute, Gambio-Optionen, bestehende Zuordnungen). */
export async function loadVariantMatrix(ctx: SettingsCtx, shopKey: string): Promise<VariantMatrix> {
  const payload = await post<VariantMatrix>(ctx, 'gambio-variant-mapping-data', { shopKey });
  if (!payload.ok) throw new Error(payload.error?.message ?? 'Matrix nicht ladbar');
  return payload;
}

/** Legt ein Vario-Variantenattribut oder einen -Wert aus einer Gambio-Vorgabe an; liefert die neue Vario-ID. */
export async function createVarioVariant(
  ctx: SettingsCtx,
  shopKey: string,
  kind: 'attribute' | 'value',
  payload: Record<string, unknown>,
): Promise<string> {
  const out = await post<{ ok?: boolean; id?: string; error?: { message: string } }>(
    ctx, 'gambio-variant-create', { shopKey, kind, ...payload },
  );
  if (!out.ok || !out.id) throw new Error(out.error?.message ?? 'Anlegen fehlgeschlagen');
  return out.id;
}

/** Speichert eine Attribut- bzw. Wert-Zuordnung (Override). Ohne _gambio_option_value_id → Attribut-Ebene. */
export async function setVariantOverride(
  ctx: SettingsCtx,
  shopKey: string,
  args: { gambioOptionId: string; varioAttributeId: string; gambioOptionValueId?: string; varioValueId?: string },
): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_set_variant_override', {
    _shop_key: shopKey,
    _gambio_option_id: args.gambioOptionId,
    _vario_attribute_id: args.varioAttributeId,
    ...(args.gambioOptionValueId ? { _gambio_option_value_id: args.gambioOptionValueId } : {}),
    ...(args.varioValueId ? { _vario_value_id: args.varioValueId } : {}),
  });
  if (error) throw new Error(error.message);
}

/** Entfernt eine Zuordnung (Attribut-Ebene, oder mit _gambio_option_value_id nur einen Wert). */
export async function clearVariantOverride(
  ctx: SettingsCtx,
  shopKey: string,
  gambioOptionId: string,
  gambioOptionValueId?: string,
): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_clear_variant_override', {
    _shop_key: shopKey,
    _gambio_option_id: gambioOptionId,
    ...(gambioOptionValueId ? { _gambio_option_value_id: gambioOptionValueId } : {}),
  });
  if (error) throw new Error(error.message);
}

// ── Varianten-Attribut-Kollisionen (Migration 0318) ──────────────────────────

/** Offene Attribut-Kollisionen. Kann strukturell nicht rejecten (try/catch um den ganzen Body): ein
 *  fehlender Hinweis darf keine Seite blockieren — weder über den Supabase-Fehlerkanal noch über einen
 *  Reject (Netzfehler, Auth-Refresh). Der geschluckte Fehler geht in die Konsole, sonst ist die
 *  dokumentierte Drift zwischen plpgsql- und TS-Fassung der Regel nicht diagnostizierbar: die RPC castet
 *  `::numeric` und läuft bei einer nicht-numerischen Options-ID in 22P02, während der Worker die
 *  Produktgruppe trotzdem blockiert und im Protokoll auf „Einstellungen → Varianten" verweist. */
export async function loadVariantConflicts(ctx: SettingsCtx, shopKey: string): Promise<ConflictRow[]> {
  try {
    const { data, error } = await ctx.supabase.rpc('gambio_get_variant_conflicts', { _shop_key: shopKey });
    if (error) {
      console.warn('[gambio] Varianten-Konflikte nicht ladbar', error);
      return [];
    }
    return (data ?? []) as ConflictRow[];
  } catch (e) {
    console.warn('[gambio] Varianten-Konflikte nicht ladbar', e);
    return [];
  }
}

/** Quittiert ein Options-Paar als „absichtlich dasselbe Attribut". Wirft: der Benutzer muss ein
 *  Scheitern mitbekommen, sonst hält er die Kollision für freigegeben. */
export async function acknowledgeVariantShare(
  ctx: SettingsCtx, shopKey: string, row: ConflictRow,
): Promise<void> {
  const { error } = await ctx.supabase.rpc('gambio_acknowledge_variant_share', {
    _shop_key: shopKey,
    _option_id_low: row.option_id_low,
    _option_id_high: row.option_id_high,
    _vario_attribute_id: row.vario_attribute_id,
  });
  if (error) throw new Error(error.message);
}

// ── Optionsverwaltung (gambio-options-crud) ──────────────────────────────────
export type OptLangMap = Record<string, string>;
export interface GambioOptionValueRow {
  valueId: string; sortOrder: number; image: string; modelNumber: string;
  price: number; weight: number; labels: OptLangMap;
}
export interface GambioOptionGroup {
  optionId: string; type: string; sortOrder: number;
  labels: OptLangMap; adminLabels: OptLangMap; values: GambioOptionValueRow[];
}
export interface OptionsListResult { languages: string[]; options: GambioOptionGroup[] }

export async function loadOptions(ctx: SettingsCtx, shopKey: string): Promise<OptionsListResult> {
  const r = await post<{ ok: boolean; languages: string[]; options: GambioOptionGroup[] }>(ctx, 'gambio-options-crud', { action: 'list', shopKey });
  return { languages: r.languages ?? [], options: r.options ?? [] };
}
export async function createOptionGroup(ctx: SettingsCtx, shopKey: string, option: { type: string; sortOrder: number; labels: OptLangMap; adminLabels: OptLangMap }): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'create-option', shopKey, option });
}
export async function updateOptionGroup(ctx: SettingsCtx, shopKey: string, optionId: string, option: { type: string; sortOrder: number; labels: OptLangMap; adminLabels: OptLangMap }): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'update-option', shopKey, optionId, option });
}
export async function deleteOptionGroup(ctx: SettingsCtx, shopKey: string, optionId: string): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'delete-option', shopKey, optionId });
}
export async function createOptionValue(ctx: SettingsCtx, shopKey: string, optionId: string, value: { sortOrder: number; labels: OptLangMap; modelNumber?: string; price?: number; weight?: number }): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'create-value', shopKey, optionId, value });
}
export async function updateOptionValue(ctx: SettingsCtx, shopKey: string, optionId: string, valueId: string, value: { sortOrder: number; labels: OptLangMap; modelNumber?: string; price?: number; weight?: number }): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'update-value', shopKey, optionId, valueId, value });
}
export async function deleteOptionValue(ctx: SettingsCtx, shopKey: string, optionId: string, valueId: string): Promise<void> {
  await post(ctx, 'gambio-options-crud', { action: 'delete-value', shopKey, optionId, valueId });
}
export async function uploadOptionValueImage(ctx: SettingsCtx, shopKey: string, optionId: string, valueId: string, fileName: string, contentType: string, base64: string): Promise<{ image: string }> {
  return await post(ctx, 'gambio-options-crud', { action: 'upload-value-image', shopKey, optionId, valueId, fileName, contentType, base64 });
}
